import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Nav from "@/components/feature/Nav";
import FloatingConsult from "@/components/feature/FloatingConsult";
import ConsultationDrawer from "@/components/feature/ConsultationDrawer";
import SectionHero from "./components/SectionHero";
import SectionConcern from "./components/SectionConcern";
import SectionResults from "./components/SectionResults";
import SectionPractice from "./components/SectionPractice";
import SectionFirstVisit from "./components/SectionFirstVisit";
import SectionTreatments from "./components/SectionTreatments";
import SectionPricingFaq from "./components/SectionPricingFaq";
import SectionBooking from "./components/SectionBooking";
import CaseDetailOverlay from "./components/CaseDetailOverlay";
import ConcernDetailOverlay from "./components/ConcernDetailOverlay";
import ParticleRibbon from "@/components/feature/ParticleRibbon";
import { SHARED_PATIENT_IMAGE } from "@/mocks/sharedPatient";
import { caseRing } from "@/mocks/home";

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const [consultOpen, setConsultOpen] = useState(false);
  const [concernIdx, setConcernIdx] = useState(0);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [concernDetailId, setConcernDetailId] = useState<string | null>(null);
  const [caseOrigin, setCaseOrigin] = useState<{ rect: DOMRect; src: string } | null>(null);

  const sharedRef = useRef<HTMLDivElement>(null);
  const sharedImgRef = useRef<HTMLImageElement>(null);
  const ribbonProgressRef = useRef(0);

  useEffect(() => {
    const shared = sharedRef.current;
    const img = sharedImgRef.current;
    if (!shared || !img) return;

    shared.style.transformOrigin = "0 0";
    shared.style.transform = "none";

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
    const clamp01 = (v: number) => clamp(v, 0, 1);
    const smootherstep = (x: number) => {
      const t = clamp01(x);
      return t * t * t * (t * (t * 6 - 15) + 10);
    };

    const getEls = () => ({
      hero: document.querySelector<HTMLElement>('[data-slot="hero"]'),
      concern: document.querySelector<HTMLElement>('[data-slot="concern"]'),
      concernImg: document.querySelector<HTMLElement>('[data-slot="concern"] img'),
      heroSection: document.getElementById("top"),
      concernSection: document.getElementById("concern"),
      resultsSection: document.getElementById("results"),
      heroCopy: document.querySelector<HTMLElement>(".hero-copy"),
    });

    // Single continuous scene: 0..1 = hero -> concern slot only.
    // The concern -> results transition has been removed; results now shows
    // a plain static image.
    let t1 = 0;
    let targetProgress = 0;
    let currentProgress = 0;
    const SMOOTHING = 0.12;
    let handedOff = false;

    let st1: ScrollTrigger | null = null;
    let stMaster: ScrollTrigger | null = null;

    const render = (p: number) => {
      const els = getEls();
      if (!els.hero || !els.concern) return;

      // Hero copy fade — tied to smoothed progress for a matching feel
      if (els.heroCopy) {
        const cp = clamp01((p - 0.10) / 0.20);
        els.heroCopy.style.opacity = String(1 - cp);
        els.heroCopy.style.transform = `translateY(${-cp * 40}px)`;
      }

      const heroR = els.hero.getBoundingClientRect();
      const concernR = els.concern.getBoundingClientRect();

      const applyAt = (u: number) => {
        const left = lerp(heroR.left, concernR.left, u);
        const top = lerp(heroR.top, concernR.top, u);
        const width = lerp(heroR.width, concernR.width, u);
        const height = lerp(heroR.height, concernR.height, u);
        const radius = lerp(16, 14, u);
        const ox = lerp(50, 65, u);
        const oy = lerp(45, 50, u);
        shared.style.left = `${left}px`;
        shared.style.top = `${top}px`;
        shared.style.width = `${width}px`;
        shared.style.height = `${height}px`;
        shared.style.borderRadius = `${radius}px`;
        img.style.objectPosition = `${ox}% ${oy}%`;
      };

      // Once we arrive at the concern slot, hand off to its native image.
      if (p >= 0.995) {
        applyAt(1);
        shared.style.opacity = "0";
        shared.style.visibility = "hidden";
        if (els.concernImg) els.concernImg.style.opacity = "1";
        handedOff = true;
        return;
      }
      if (handedOff) {
        applyAt(1);
        shared.style.opacity = "1";
        shared.style.visibility = "visible";
        if (els.concernImg) els.concernImg.style.opacity = "0";
        handedOff = false;
      }

      applyAt(smootherstep(p));
    };

    const computeTarget = () => {
      targetProgress = clamp01(t1);
    };

    let rafId = 0;
    let running = true;
    const loop = () => {
      const diff = targetProgress - currentProgress;
      if (Math.abs(diff) < 0.0002) {
        currentProgress = targetProgress;
      } else {
        const step = diff * SMOOTHING;
        const capped = clamp(step, -0.06, 0.06);
        currentProgress += capped;
      }
      render(currentProgress);
      if (running) rafId = requestAnimationFrame(loop);
    };

    const buildTriggers = () => {
      const els = getEls();
      if (!els.heroSection || !els.concernSection || !els.resultsSection) return;
      st1?.kill();
      stMaster?.kill();

      st1 = ScrollTrigger.create({
        trigger: els.heroSection,
        start: "top top",
        endTrigger: els.concernSection,
        end: "top 22%",
        invalidateOnRefresh: true,
        onUpdate: (self) => { t1 = self.progress; computeTarget(); },
      });

      stMaster = ScrollTrigger.create({
        trigger: els.heroSection,
        start: "top top",
        endTrigger: els.resultsSection,
        end: "bottom 20%",
        invalidateOnRefresh: true,
        onUpdate: (self) => { ribbonProgressRef.current = self.progress; },
      });
    };

    buildTriggers();
    computeTarget();
    currentProgress = targetProgress;
    rafId = requestAnimationFrame(loop);

    const to1 = setTimeout(() => { ScrollTrigger.refresh(); computeTarget(); }, 200);
    const to2 = setTimeout(() => { ScrollTrigger.refresh(); computeTarget(); }, 800);

    const onResize = () => { ScrollTrigger.refresh(); computeTarget(); };
    window.addEventListener("resize", onResize);

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      st1?.kill();
      stMaster?.kill();
      clearTimeout(to1);
      clearTimeout(to2);
    };
  }, []);

  return (
    <div className="relative">
      <Nav onOpenConsult={() => setConsultOpen(true)} />

      {/* Single shared patient image layer (fixed) */}
      <div
        ref={sharedRef}
        className="fixed overflow-hidden pointer-events-none"
        style={{ top: 0, left: 0, zIndex: 40, willChange: "left, top, width, height" }}
      >
        <img
          ref={sharedImgRef}
          src={SHARED_PATIENT_IMAGE}
          alt="Nordin Dental patient in consultation room"
          className="w-full h-full object-cover block"
          style={{ objectPosition: "50% 45%" }}
        />
      </div>

      <SectionHero onOpenConsult={() => setConsultOpen(true)} />
      <SectionConcern
        activeIndex={concernIdx}
        onSelect={setConcernIdx}
        onOpenConsult={() => setConsultOpen(true)}
        onOpenDetail={(id) => setConcernDetailId(id)}
      />
      <SectionResults onSelectCase={(id, rect, src) => { setCaseOrigin({ rect, src }); setCaseId(id); }} />
      <SectionPractice />
      <SectionFirstVisit />
      <SectionTreatments />
      <SectionPricingFaq onOpenConsult={() => setConsultOpen(true)} />
      <SectionBooking onOpenConsult={() => setConsultOpen(true)} />

      <ParticleRibbon progressRef={ribbonProgressRef} />

      <FloatingConsult onClick={() => setConsultOpen(true)} hidden={consultOpen} />
      <ConsultationDrawer open={consultOpen} onClose={() => setConsultOpen(false)} />
      <ConcernDetailOverlay
        concernId={concernDetailId}
        onClose={() => setConcernDetailId(null)}
        onOpenConsult={() => { setConcernDetailId(null); setConsultOpen(true); }}
      />
      <CaseDetailOverlay
        caseId={caseId}
        origin={caseOrigin}
        onClose={() => { setCaseId(null); setCaseOrigin(null); }}
        getCurrentOriginRect={(id) => {
          const idx = caseRing.findIndex((x) => x.id === id);
          if (idx < 0) return null;
          const el = document.querySelector<HTMLElement>(`[data-ring-index="${idx}"]`);
          const target = el?.querySelector("img") ?? el;
          return target ? target.getBoundingClientRect() : null;
        }}
      />
    </div>
  );
}